/**
 * Jovii — what this browser remembers.
 *
 * Owned by the CATALOGUE UI agent, but deliberately PUBLIC: the PLAYER UI reads
 * `prefs.audioLang` / `prefs.subtitleLang` to choose tracks, and calls
 * `recordProgress()` as playback advances. Everything lives in localStorage; a
 * fresh browser is a working browser.
 *
 * Runes at module scope, so any component can read `prefs.name` and stay reactive.
 */

import { untrack } from 'svelte';
import type { CatalogItem, ContentRef, MediaKind, QualityTier } from '$types';
import { KEYS, readJson, writeJson } from './storage';

/**
 * Every mutator below is read-modify-write: it filters the current list, puts the
 * new entry on the front, and writes the list back. Done in a tracking context —
 * and `$effect` is exactly where "we are now in room X" gets recorded — the READ
 * subscribes the caller to the very state the WRITE then changes, and the effect
 * re-runs for ever. `rememberRoom` stamps `Date.now()`, so the value never even
 * settles: it ran 1003 times and Svelte tore the screen down with
 * `effect_update_depth_exceeded`, which took every group session with it.
 *
 * So every mutator runs wholly untracked, HERE. Guarding at the call sites instead
 * would mean remembering to do it at every future one — and the read to guard is
 * not only the obvious `.filter()`: the `$state.snapshot()` inside the persist step
 * subscribes just as effectively, which is what survived the first attempt at this.
 *
 * These are imperative commands, not derivations. Nothing they read should ever
 * make the caller re-run.
 */
function commit(mutate: () => void): void {
  untrack(mutate);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Preferences
 * ──────────────────────────────────────────────────────────────────────────── */

export interface Preferences {
  /** Display name in a room. Empty until the user says otherwise. */
  name: string;
  /** Preferred audio language, ISO 639 style ("eng"). '' = whatever the file defaults to. */
  audioLang: string;
  /** Preferred subtitle language. '' = none. */
  subtitleLang: string;
  /** Show subtitles when a matching track exists. */
  subtitlesOn: boolean;
  /** Never auto-select a candidate above this. 2160p files are 40 GB+ and mostly HEVC. */
  maxQuality: Exclude<QualityTier, 'unknown'>;
  /** Verify the top candidates in the background the moment a title page opens. */
  eagerVerify: boolean;
}

/* English audio, English subtitles, subtitles ON — the owner's defaults for
   everybody (2026-08-17), standing until the person changes them. */
const DEFAULT_PREFS: Preferences = {
  name: '',
  audioLang: 'eng',
  subtitleLang: 'eng',
  subtitlesOn: true,
  maxQuality: '1080p',
  eagerVerify: true
};

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function strings(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(x => typeof x === 'string');
}
function finite(value: unknown): value is number { return typeof value === 'number' && Number.isFinite(value); }
function optionalStrings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every(key => value[key] === undefined || typeof value[key] === 'string');
}
function isCatalogItem(value: unknown): value is CatalogItem {
  return record(value) && typeof value.id === 'string' && typeof value.name === 'string'
    && ['movie', 'series'].includes(value.kind as string)
    && optionalStrings(value, ['year', 'poster', 'background', 'logo', 'description', 'imdbRating', 'runtimeText', 'releaseInfo'])
    && (value.genres === undefined || strings(value.genres))
    && (value.trailers === undefined || strings(value.trailers));
}
function catalogItems(value: unknown): CatalogItem[] {
  return Array.isArray(value) ? value.filter(isCatalogItem) : [];
}
function decodePreferences(value: unknown): Partial<Preferences> & { v?: number } {
  if (!record(value) || (value.v !== undefined && value.v !== 1 && value.v !== 2)) return {};
  const out: Partial<Preferences> & { v?: number } = {};
  if (value.v === 1 || value.v === 2) out.v = value.v;
  for (const key of ['name', 'audioLang', 'subtitleLang'] as const) {
    if (typeof value[key] === 'string') out[key] = value[key];
  }
  for (const key of ['subtitlesOn', 'eagerVerify'] as const) {
    if (typeof value[key] === 'boolean') out[key] = value[key];
  }
  if (['480p', '720p', '1080p', '2160p'].includes(value.maxQuality as string)) out.maxQuality = value.maxQuality as Preferences['maxQuality'];
  return out;
}
const loaded = readJson(KEYS.prefs, {}, decodePreferences);
// Earlier track picks could accidentally persist an unknown tag as a global
// language. That is not a selectable preference; restore the established default.
if (/^(und|unknown|mis)$/i.test(loaded.audioLang?.trim() ?? '')) loaded.audioLang = 'eng';
if (/^(und|unknown|mis)$/i.test(loaded.subtitleLang?.trim() ?? '')) loaded.subtitleLang = 'eng';

/*
 * ONE-TIME PROMOTION to the English-first defaults. A snapshot written before
 * this shipped stores '' and false for these fields whenever ANY preference
 * was changed — that '' froze the old default, it was never a choice. So a
 * pre-v2 snapshot is promoted once; the `v` marker then keeps every later
 * deliberate setting (including "no subtitles") exactly as the person left it.
 */
if ((loaded.v ?? 1) < 2) {
  if (!loaded.audioLang) loaded.audioLang = 'eng';
  if (!loaded.subtitleLang) {
    loaded.subtitleLang = 'eng';
    loaded.subtitlesOn = true;
  }
}

/** Live preferences. Mutate fields directly; the write-back is automatic. */
export const prefs: Preferences = $state({ ...DEFAULT_PREFS, ...loaded });

let prefsDirty = false;
$effect.root(() => {
  $effect(() => {
    // Touch every field so the effect re-runs on any change.
    const snapshot: Preferences = {
      name: prefs.name,
      audioLang: prefs.audioLang,
      subtitleLang: prefs.subtitleLang,
      subtitlesOn: prefs.subtitlesOn,
      maxQuality: prefs.maxQuality,
      eagerVerify: prefs.eagerVerify
    };
    // `v: 2` marks a snapshot that has been through the English-first
    // promotion above, so it never runs twice.
    if (prefsDirty) writeJson(KEYS.prefs, { ...snapshot, v: 2 });
    prefsDirty = true;
  });
});

/* ────────────────────────────────────────────────────────────────────────────
 * Resume points / continue watching
 * ──────────────────────────────────────────────────────────────────────────── */

/** One thing this browser has started watching. */
export interface Progress {
  /** `movie:tt0133093` or `series:tt0903747:1:1`. */
  key: string;
  kind: MediaKind;
  imdbId: string;
  season?: number;
  episode?: number;
  /** Series name, or film title. */
  title: string;
  /** Episode title, when this is an episode. */
  subtitle?: string;
  poster?: string;
  /** Wide art for the Continue card. */
  background?: string;
  positionSec: number;
  durationSec: number;
  updatedAt: number;
  /** True once watched past the end threshold; hidden from Continue. */
  finished: boolean;
}

const MAX_PROGRESS = 60;
/** Below this we have not really started, and the entry is noise. */
const MIN_RESUME_SEC = 30;
/** Past this fraction the thing is finished — offer the next episode instead. */
const FINISHED_AT = 0.94;

export const progress: { items: Progress[] } = $state({
  items: readJson<Progress[]>(KEYS.progress, [], value => Array.isArray(value) ? value.filter((p): p is Progress =>
    record(p) && typeof p.key === 'string' && typeof p.imdbId === 'string' && typeof p.title === 'string'
    && ['movie', 'series'].includes(p.kind as string)
    && optionalStrings(p, ['subtitle', 'poster', 'background'])
    && ['season', 'episode'].every(key => p[key] === undefined || (Number.isSafeInteger(p[key]) && (p[key] as number) >= 0))
    && finite(p.positionSec) && p.positionSec >= 0 && finite(p.durationSec) && p.durationSec >= 0
    && finite(p.updatedAt) && Math.abs(p.updatedAt) <= 8.64e15 && typeof p.finished === 'boolean'
  ).slice(0, MAX_PROGRESS) : [])
});

export function progressKey(ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>): string {
  return ref.kind === 'series'
    ? `series:${ref.imdbId}:${ref.season ?? 1}:${ref.episode ?? 1}`
    : `movie:${ref.imdbId}`;
}

function persistProgress(): void {
  writeJson(KEYS.progress, $state.snapshot(progress.items));
}

/** Look up a resume point. Returns undefined when there is nothing to resume. */
export function resumeFor(ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>): Progress | undefined {
  const key = progressKey(ref);
  return progress.items.find((p) => p.key === key);
}

/** Seconds to start at: the resume point, or 0. Finished things restart. */
export function resumeSec(ref: Pick<ContentRef, 'kind' | 'imdbId' | 'season' | 'episode'>): number {
  const found = resumeFor(ref);
  if (!found || found.finished) return 0;
  return found.positionSec >= MIN_RESUME_SEC ? found.positionSec : 0;
}

/** The most recent episode of a series that was started, for "continue with S2 E4". */
export function lastEpisodeOf(imdbId: string): Progress | undefined {
  return progress.items
    .filter((p) => p.imdbId === imdbId && p.kind === 'series')
    .sort((a, b) => b.updatedAt - a.updatedAt)[0];
}

export interface ProgressInput {
  ref: ContentRef;
  positionSec: number;
  durationSec: number;
  title: string;
  subtitle?: string;
  poster?: string;
  background?: string;
}

/**
 * Record where playback got to. Called by the PLAYER UI, throttled to ~1 Hz by
 * the caller — this function itself is cheap but writes to localStorage.
 */
export function recordProgress(input: ProgressInput): void {
  const { ref, positionSec, durationSec } = input;
  if (!Number.isFinite(positionSec) || positionSec < 0 || !Number.isFinite(durationSec) || durationSec < 0) return;
  const key = progressKey(ref);
  const finished = durationSec > 0 && positionSec / durationSec >= FINISHED_AT;

  const entry: Progress = {
    key,
    kind: ref.kind,
    imdbId: ref.imdbId,
    ...(ref.season !== undefined ? { season: ref.season } : {}),
    ...(ref.episode !== undefined ? { episode: ref.episode } : {}),
    title: input.title,
    ...(input.subtitle ? { subtitle: input.subtitle } : {}),
    ...(input.poster ? { poster: input.poster } : {}),
    ...(input.background ? { background: input.background } : {}),
    positionSec,
    durationSec,
    updatedAt: Date.now(),
    finished
  };

  commit(() => {
    const rest = progress.items.filter((p) => p.key !== key);
    rest.unshift(entry);
    progress.items = rest.slice(0, MAX_PROGRESS);
    persistProgress();
  });
}

/** Drop one resume point (the "remove from Continue" action). */
export function forgetProgress(key: string): void {
  commit(() => {
    progress.items = progress.items.filter((p) => p.key !== key);
    persistProgress();
  });
}

/** The Continue row: started, not finished, newest first. */
export function continueWatching(limit = 12): Progress[] {
  return progress.items
    .filter((p) => !p.finished && p.positionSec >= MIN_RESUME_SEC)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, limit);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Recent searches
 * ──────────────────────────────────────────────────────────────────────────── */

export const recent: { queries: string[]; items: CatalogItem[] } = $state(
  readJson<{ queries: string[]; items: CatalogItem[] }>(KEYS.recent, { queries: [], items: [] }, value => ({
    queries: record(value) && Array.isArray(value.queries) ? value.queries.filter((q): q is string => typeof q === 'string').slice(0, 8) : [],
    items: record(value) ? catalogItems(value.items).slice(0, 20) : []
  }))
);

function persistRecent(): void {
  writeJson(KEYS.recent, $state.snapshot(recent));
}

export function rememberQuery(q: string): void {
  const query = q.trim();
  if (query.length < 2) return;
  commit(() => {
    recent.queries = [query, ...recent.queries.filter((x) => x.toLowerCase() !== query.toLowerCase())].slice(0, 8);
    persistRecent();
  });
}

/** One recent search, taken back — the row's own ×, beside Clear-all. */
export function forgetQuery(q: string): void {
  commit(() => {
    recent.queries = recent.queries.filter((x) => x.toLowerCase() !== q.trim().toLowerCase());
    persistRecent();
  });
}

export function clearQueries(): void {
  commit(() => {
    recent.queries = [];
    persistRecent();
  });
}

/** Opening a title puts it in "Recently opened", which is the browse fallback. */
export function rememberItem(item: CatalogItem): void {
  commit(() => {
    recent.items = [item, ...recent.items.filter((x) => x.id !== item.id)].slice(0, 20);
    persistRecent();
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Saved — the "My Jovii" shelf
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Deliberately a LIST OF TITLES and not a list of ids: My Jovii has to draw the
 * shelf with no network at all, and a list of ids would mean 40 Cinemeta lookups
 * before the screen could say anything. A `CatalogItem` is ~300 bytes.
 *
 * Per browser, like everything else here. When accounts arrive (docs/SOCIAL.md)
 * this is the obvious first thing to carry into them.
 */
const MAX_SAVED = 200;

export const saved: { items: CatalogItem[] } = $state({
  items: readJson<CatalogItem[]>(KEYS.saved, [], value => catalogItems(value).slice(0, MAX_SAVED))
});

function persistSaved(): void {
  writeJson(KEYS.saved, $state.snapshot(saved.items));
}

export function isSaved(id: string): boolean {
  return saved.items.some((x) => x.id === id);
}

/** Save, or unsave. Returns the state it ended in, for the control that called it. */
export function toggleSaved(item: CatalogItem): boolean {
  let nowSaved = false;
  commit(() => {
    const rest = saved.items.filter((x) => x.id !== item.id);
    if (rest.length === saved.items.length) {
      rest.unshift(item);
      nowSaved = true;
    }
    saved.items = rest.slice(0, MAX_SAVED);
    persistSaved();
  });
  return nowSaved;
}

export function forgetSaved(id: string): void {
  commit(() => {
    saved.items = saved.items.filter((x) => x.id !== id);
    persistSaved();
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Ratings — the thumb on a title page
 *
 * A THUMB IS NOT A STAR AND IT IS NOT A RECOMMENDATION ENGINE. Nothing reads
 * this yet except the control that writes it: there is no server to send it to
 * (this app has no telemetry and no engagement metric to farm — see
 * docs/DESIGN-LANGUAGE.md § 1) and no model behind it. It is a note to yourself
 * about a film you finished, kept the way every other note in this app is kept:
 * in this browser, in localStorage, per title id.
 *
 * A RECORD AND NOT A LIST, because the only question ever asked of it is "what
 * did I say about tt0133093" — an O(1) lookup that a list of 200 would make a
 * scan. `null` is a legal stored value and means the same as absent; the
 * decoder keeps it so a future "seen but unrated" state has somewhere to live
 * without a schema change.
 * ──────────────────────────────────────────────────────────────────────────── */

export type Rating = 'up' | 'down';

/** How many titles this browser remembers a thumb for. Beyond this the oldest go. */
const MAX_RATED = 400;

function ratingRecord(value: unknown): Record<string, Rating> {
  if (!record(value)) return {};
  const out: Record<string, Rating> = {};
  let kept = 0;
  for (const [id, verdict] of Object.entries(value)) {
    if (kept >= MAX_RATED) break;
    // A stored `null` is "no opinion", which is what absence already means.
    if (verdict !== 'up' && verdict !== 'down') continue;
    if (typeof id !== 'string' || id.length === 0 || id.length > 32) continue;
    out[id] = verdict;
    kept += 1;
  }
  return out;
}

export const rated: { items: Record<string, Rating> } = $state({
  items: readJson<Record<string, Rating>>(KEYS.rated, {}, ratingRecord)
});

/** What this browser said about a title, or null. */
export function ratingOf(id: string): Rating | null {
  return rated.items[id] ?? null;
}

/**
 * Set, or take back. A second press of the same thumb clears it — the control
 * is a toggle, not a radio pair, because "I pressed the wrong one" and "I no
 * longer think that" are the same gesture. Returns the state it ended in.
 */
export function rate(id: string, verdict: Rating): Rating | null {
  let next: Rating | null = null;
  commit(() => {
    const current = rated.items[id] ?? null;
    next = current === verdict ? null : verdict;
    const items = { ...rated.items };
    if (next === null) delete items[id];
    else items[id] = next;
    rated.items = items;
    writeJson(KEYS.rated, $state.snapshot(rated.items));
  });
  return next;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Rooms this browser has been in — DELIBERATELY GONE.
 *
 * This kept the last six room codes so Settings and the Rooms screen could offer
 * them as links. Every one of those links was a dead address: a room ends the
 * instant the last person leaves the player and its code is never reissued, so
 * the list could only ever invite somebody into "that room has ended". It is
 * also the thing the owner was seeing when he reported that a room he had left
 * was "still available to come back to".
 *
 * The device-local room recorder that replaced those links is also retired.
 * Browser playback progress remains separate and still supports Continue watching
 * and My Jovii. Old room-recorder bytes are left untouched, with no migration.
 * ──────────────────────────────────────────────────────────────────────────── */
